import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, X, Thermometer, Edit3 } from "lucide-react";
import { api, formatApiError } from "@/lib/apiClient";

const BLANK = { label: "", min_temp_c: null, max_temp_c: null, recommend_categories: [], recommend_flavor_ids: [], is_active: true, sort_order: 0 };

export default function AdminHeatRules() {
  const [rules, setRules] = useState([]);
  const [flavors, setFlavors] = useState([]);
  const [worlds, setWorlds] = useState([]);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [override, setOverride] = useState({ flavor_ids: [], note: "" });

  const load = () => {
    api.get("/admin/heat-rules").then((r) => setRules(r.data || []));
    api.get("/admin/heat-override").then((r) => setOverride(r.data || { flavor_ids: [], note: "" }));
  };
  useEffect(() => {
    load();
    api.get("/admin/flavors").then((r) => setFlavors(r.data || []));
    api.get("/admin/worlds").then((r) => setWorlds(r.data || []));
  }, []);

  const save = async () => {
    if (!editing.label?.trim()) return toast.error("Label is required");
    const body = { ...editing };
    delete body.id; delete body.created_at;
    try {
      if (editing.id) await api.put(`/admin/heat-rules/${editing.id}`, body);
      else await api.post("/admin/heat-rules", body);
      toast.success("Rule saved"); setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this rule?")) return;
    await api.delete(`/admin/heat-rules/${id}`); load();
  };

  const saveOverride = async () => {
    try { await api.put("/admin/heat-override", override); toast.success("Override saved"); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const clearOverride = async () => {
    setOverride({ flavor_ids: [], note: "" });
    await api.put("/admin/heat-override", { flavor_ids: [], note: "" });
    toast.success("Override cleared. Rules will apply automatically.");
  };

  const toggleCat = (slug) => setEditing((e) => ({ ...e, recommend_categories: e.recommend_categories.includes(slug) ? e.recommend_categories.filter((x) => x !== slug) : [...e.recommend_categories, slug] }));
  const toggleFlavor = (fid) => setEditing((e) => ({ ...e, recommend_flavor_ids: e.recommend_flavor_ids.includes(fid) ? e.recommend_flavor_ids.filter((x) => x !== fid) : [...e.recommend_flavor_ids, fid] }));

  return (
    <div data-testid="admin-heat" className="space-y-10">
      <header className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">Heat-based recommendations</div>
          <h1 className="editorial-h1 text-5xl mt-2">Hot day? Cold day?</h1>
          <p className="text-sm text-brand-charcoal/65 mt-2 max-w-xl">Set temperature-based rules. The website will suggest matching flavours. You can also manually override these.</p>
        </div>
        <button onClick={() => { setEditing({ ...BLANK }); setOpen(true); }} className="btn-pill btn-dark text-sm" data-testid="ah-new"><Plus size={16} /> New rule</button>
      </header>

      {/* Manual override card */}
      <div className="card-elevated p-6" data-testid="ah-override">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[11px] uppercase tracking-[0.22em] text-brand-red">Manual override</div>
            <div className="font-display text-2xl mt-1">Force these flavours, regardless of temperature</div>
            <p className="text-xs text-brand-charcoal/65 mt-1">Useful for festivals, restocks, special days. Clear to fall back to rules.</p>
          </div>
          <div className="flex gap-2">
            <button onClick={saveOverride} className="btn-pill btn-dark text-xs" data-testid="ah-override-save">Save override</button>
            <button onClick={clearOverride} className="btn-pill btn-ghost border border-brand-line text-xs">Clear</button>
          </div>
        </div>
        <input className="pp-input mt-4" placeholder="Internal note (optional)" value={override.note || ""} onChange={(e) => setOverride({ ...override, note: e.target.value })} />
        <div className="mt-4 flex flex-wrap gap-2">
          {flavors.map((f) => {
            const active = override.flavor_ids.includes(f.id);
            return (
              <button key={f.id} onClick={() => setOverride({ ...override, flavor_ids: active ? override.flavor_ids.filter((x) => x !== f.id) : [...override.flavor_ids, f.id] })}
                className={`btn-pill text-xs ${active ? "btn-dark" : "btn-ghost border border-brand-line"}`}>{f.name}</button>
            );
          })}
        </div>
      </div>

      {/* Rules list */}
      <div>
        <div className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55 mb-3 flex items-center gap-2"><Thermometer size={12} /> Temperature rules ({rules.length})</div>
        <div className="grid md:grid-cols-2 gap-4">
          {rules.map((r) => (
            <div key={r.id} className="card-elevated p-5" data-testid={`ah-rule-${r.id}`}>
              <div className="flex items-center justify-between">
                <div className="font-display text-xl">{r.label}</div>
                <div className="text-[11px] uppercase tracking-[0.22em] text-brand-red">{r.min_temp_c ?? "−∞"}°C – {r.max_temp_c ?? "+∞"}°C</div>
              </div>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {(r.recommend_categories || []).map((c) => (
                  <span key={c} className="text-[10px] uppercase tracking-[0.18em] border border-brand-line rounded-full px-2 py-0.5">{c}</span>
                ))}
              </div>
              <div className="mt-3 flex gap-2">
                <button onClick={() => { setEditing({ ...r }); setOpen(true); }} className="btn-pill btn-ghost border border-brand-line text-xs"><Edit3 size={12} /> Edit</button>
                <button onClick={() => del(r.id)} className="btn-pill btn-ghost text-xs text-brand-red"><Trash2 size={12} /></button>
              </div>
            </div>
          ))}
          {rules.length === 0 && <div className="text-brand-charcoal/60 py-6">No rules yet.</div>}
        </div>
      </div>

      {open && editing && (
        <div className="fixed inset-0 z-50 bg-brand-charcoal/40 grid place-items-center p-4">
          <div className="bg-white rounded-3xl w-full max-w-3xl max-h-[90vh] overflow-y-auto p-8">
            <div className="flex items-center justify-between mb-4">
              <div className="font-display text-3xl">{editing.id ? "Edit rule" : "New rule"}</div>
              <button onClick={() => setOpen(false)}><X /></button>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2"><label className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">Rule label</label><input className="pp-input" value={editing.label} onChange={(e) => setEditing({ ...editing, label: e.target.value })} placeholder="e.g. Scorcher (above 35°C)" /></div>
              <div><label className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">Min temperature °C (blank = no minimum)</label><input type="number" className="pp-input" value={editing.min_temp_c ?? ""} onChange={(e) => setEditing({ ...editing, min_temp_c: e.target.value === "" ? null : Number(e.target.value) })} /></div>
              <div><label className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">Max temperature °C (blank = no maximum)</label><input type="number" className="pp-input" value={editing.max_temp_c ?? ""} onChange={(e) => setEditing({ ...editing, max_temp_c: e.target.value === "" ? null : Number(e.target.value) })} /></div>
              <div><label className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">Sort order</label><input type="number" className="pp-input" value={editing.sort_order || 0} onChange={(e) => setEditing({ ...editing, sort_order: Number(e.target.value) || 0 })} /></div>
              <div><label className="flex items-center gap-2 text-sm mt-6"><input type="checkbox" checked={!!editing.is_active} onChange={(e) => setEditing({ ...editing, is_active: e.target.checked })} /> Active</label></div>
            </div>

            <div className="mt-6">
              <div className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55 mb-2">Recommend categories</div>
              <div className="flex flex-wrap gap-2">
                {worlds.map((w) => {
                  const active = editing.recommend_categories.includes(w.slug);
                  return <button type="button" key={w.slug} onClick={() => toggleCat(w.slug)} className={`btn-pill text-xs ${active ? "btn-dark" : "btn-ghost border border-brand-line"}`}>{w.label}</button>;
                })}
              </div>
            </div>

            <div className="mt-6">
              <div className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55 mb-2">… or specific flavours (optional)</div>
              <div className="flex flex-wrap gap-2 max-h-40 overflow-y-auto">
                {flavors.map((f) => {
                  const active = editing.recommend_flavor_ids.includes(f.id);
                  return <button type="button" key={f.id} onClick={() => toggleFlavor(f.id)} className={`btn-pill text-xs ${active ? "btn-dark" : "btn-ghost border border-brand-line"}`}>{f.name}</button>;
                })}
              </div>
            </div>

            <div className="mt-8 flex justify-end gap-3">
              <button onClick={() => setOpen(false)} className="btn-pill btn-ghost text-sm">Cancel</button>
              <button onClick={save} className="btn-pill btn-dark text-sm">Save rule</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
